import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-common-challenges';

/**
 * Common-challenges section listing pain points as a red-dot bulleted list inside a grey panel.
 * Single full-width column with heading, optional intro text, and a vertical bullet list using custom red dot markers; the panel uses a full-width grey background with rounded corners.
 * Only type in the `common-challenges` folder; uses red-dot bullets (vs. the green checks in `best-practices/type-1`) to visually signal problems rather than recommendations.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for common challenges related to {keyword}. 6-10 words.
 * @slot text - <me-text>
 * @example
 *   <sn-common-challenges
 *     items='[{"text":"High per-user costs."},{"text":"Limited multi-party routing."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Common Challenges</me-heading>
 *     <me-text slot="text">Teams frequently encounter these pain points.</me-text>
 *   </sn-common-challenges>
 */
@Component({
  tag: 'sn-common-challenges',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcCommonChallenges {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * List of challenge items displayed as red-dot bullets.
   * Each item can be a plain string or an object with a `text` property.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} text - One challenge or pain point that {keyword} solves. You MUST write 15-30 words.
   * @item.required text
   * @item.content text
   * @minItems 4
   * @maxItems 4
   */
  @Prop({ mutable: true }) items?: (string | { text: string })[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `items` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML (also used as innerHTML for text)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} text - Fallback body text rendered as innerHTML
   * @property {array} items - Fallback bullet list (use items prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        text?: string;
        raw_html?: string;
        items?: string[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items  = parseJsonProp(this.items);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const items = Array.isArray(this.items)
      ? this.items
      : (Array.isArray(values?.items) ? values.items : []);
    const textContent = values?.text;

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__wrapper`}>
          <me-row direction="column" directionLg="column">
            <me-column spanLg="12">
              <slot name="heading">
                {values?.title && <me-heading variant="4" tag="h4">{values.title}</me-heading>}
              </slot>
              <slot name="text">
                {textContent && <me-text innerHTML={textContent}></me-text>}
              </slot>
              {items.length > 0 && (
                <ul class={`${COMPONENT_NAME}__list`}>
                  {items.map((item) => {
                    const text = typeof item === 'string' ? item : item?.text;
                    return text ? (
                      <li class={`${COMPONENT_NAME}__item`}>
                        <span class={`${COMPONENT_NAME}__bullet`} aria-hidden="true"></span>
                        <me-text tag="span">{text}</me-text>
                      </li>
                    ) : null;
                  })}
                </ul>
              )}
            </me-column>
          </me-row>
        </div>
      </me-section>
    );
  }
}
